// coreValues.js — OUR VALUES (semicircle carousel)
// Полный рабочий файл. Главное: радиус «полки» (белой дуги под каруселью)
// берётся из data-ellipse-k → задаёт вертикальный радиус эллипса (ry),
// а горизонтальный радиус (rx) доводим до края (чуть > половины ширины viewBox),
// чтобы без микроскопической щели у краёв.

export default function initCoreValues() {
    const root = document.querySelector('.values');
    if (!root) return;

    // ---- быстрые подстановки из URL (?edge=..., ?start=..., ?debug=1) ----
    const sp = new URLSearchParams(location.search);
    if (sp.has('debug'))       root.dataset.debug = '1';
    if (sp.has('arcApex'))     root.dataset.arcApex = sp.get('arcApex');
    if (sp.has('arcCyExtra'))  root.dataset.arcCyExtra = sp.get('arcCyExtra');
    if (sp.has('start'))       root.dataset.startDeg = sp.get('start');
    if (sp.has('end'))         root.dataset.endDeg   = sp.get('end');
    if (sp.has('lift'))        root.dataset.lift     = sp.get('lift');
    if (sp.has('edge'))        root.dataset.edge     = sp.get('edge');

    // ---- DOM ссылки ----
    const stage   = root.querySelector('.values__stage');
    const rail    = root.querySelector('.values__rail');
    const items   = Array.from(root.querySelectorAll('.values__item'));
    const copyEl  = root.querySelector('.values__copy');
    const arcSvg  = root.querySelector('.values__arc');
    const arcEllipse = arcSvg?.querySelector('#values-arc-ellipse');
    const arcDash    = arcSvg?.querySelector('#values-arc-dash');

    // ---- состояния ----
    let active = Math.max(0, items.findIndex(el => el.classList.contains('is-active')));
    const N    = items.length;
    const debug = root.dataset.debug === '1';

    // ---- autoplay ----
    const autoplay = root.dataset.autoplay !== '0';
    const interval = Math.max(1000, Number(root.dataset.interval || 8000));
    root.style.setProperty('--interval', `${interval}ms`);
    let timer = null;

    function startAutoplay() {
        stopAutoplay();
        timer = setInterval(() => animateTo((active + 1) % N), interval);
    }
    function stopAutoplay() { if (timer) clearInterval(timer); timer = null; }

    // ---- брейкпоинт-конфиг (подогнан под твои значения) ----
    function modeConfig(w) {
        const URL_EDGE = Number(root.dataset.edge || sp.get('edge') || NaN);

        const L_DEF = Number(root.dataset.lift || 0);
        const L_D = Number(root.dataset.liftD || NaN);
        const L_L = Number(root.dataset.liftL || NaN);
        const L_T = Number(root.dataset.liftT || NaN);
        const L_M = Number(root.dataset.liftM || NaN);

        const E_D = Number(root.dataset.edgeD || NaN);
        const E_L = Number(root.dataset.edgeL || NaN);
        const E_T = Number(root.dataset.edgeT || NaN);
        const E_M = Number(root.dataset.edgeM || NaN);

        const EDGE = def => Number.isFinite(URL_EDGE) ? URL_EDGE : def;

        if (w >= 1600) {
            return {
                visible: 5, start: Number(root.dataset.startDeg || 200), end: Number(root.dataset.endDeg || 340),
                item: 200, act: 340,
                lift: Number.isFinite(L_D) ? L_D : L_DEF,
                apex: Number(root.dataset.arcApex || 160), cyExtra: Number(root.dataset.arcCyExtra || 120),
                edge: EDGE(Number.isFinite(E_D) ? E_D : 100),
                showArc: true
            };
        } else if (w >= 1280) {
            return {
                visible: 5, start: 200, end: 340,
                item: 150, act: 250,
                lift: Number.isFinite(L_L) ? L_L : L_DEF,
                apex: 160, cyExtra: 118,
                edge: EDGE(Number.isFinite(E_L) ? E_L : 74),
                showArc: true
            };
        } else if (w >= 1024) {
            return {
                visible: 5, start: 200, end: 340,
                item: 140, act: 223,
                lift: Number.isFinite(L_T) ? L_T : L_DEF,
                apex: 156, cyExtra: 110,
                edge: EDGE(Number.isFinite(E_T) ? E_T : 56),
                showArc: true
            };
        } else if (w >= 744) {
            // планшет — дуга есть, но меньше
            return {
                visible: 5, start: 200, end: 340,
                item: 80, act: 125,
                lift: Number.isFinite(L_T) ? L_T : L_DEF,
                apex: 150, cyExtra: 100,
                edge: EDGE(Number.isFinite(E_T) ? E_T : 40),
                showArc: true
            };
        } else {
            // мобилка — линейная раскладка (как у тебя), дугу можно скрыть
            return {
                visible: 3, start: 215, end: 325,
                item: 138, act: 220,
                lift: Number.isFinite(L_M) ? L_M : L_DEF,
                apex: 146, cyExtra: 104,
                edge: EDGE(Number.isFinite(E_M) ? E_M : 26),
                showArc: false,
                layout: 'line',
                lineGap: 16,
                baseline: null
            };
        }
    }

    // окно видимых индексов вокруг активного
    function windowIndices(active, N, visible) {
        const half = Math.floor(visible / 2);
        const out = [];
        for (let k = -half; k <= half; k++) out.push((active + k + N) % N);
        return out;
    }

    // отладочный SVG (по желанию)
    let dbgSvg = null;
    function ensureDebugSvg() {
        if (!debug) return;
        if (dbgSvg) dbgSvg.remove();
        dbgSvg = document.createElementNS('http://www.w3.org/2000/svg', 'svg');
        dbgSvg.setAttribute('class', 'values-debug');
        dbgSvg.setAttribute('width', '100%');
        dbgSvg.setAttribute('height', '100%');
        dbgSvg.style.position = 'absolute';
        dbgSvg.style.inset = '0';
        dbgSvg.style.zIndex = '5';
        stage.appendChild(dbgSvg);
    }

    // геометрия
    const toRad = deg => (deg * Math.PI) / 180;

    function render() {
        const cfg = modeConfig(window.innerWidth);

        // показать/скрыть дугу (SVG-маску)
        if (arcSvg) arcSvg.style.display = cfg.showArc ? '' : 'none';

        // размеры сцены
        const rect = stage.getBoundingClientRect();
        const W = rect.width;
        const H = rect.height;

        // центр эллипса (внизу), радиусы эллипса маски (ВАЖНО: тут правим «радиус полки»)
        // Горизонтальный радиус — половина ширины (чуть больше для отсутствия щели).
        // Вертикальный радиус — то, что ты хочешь менять: ry = rx * k, где k = data-ellipse-k.
        const k = Number(root.dataset.ellipseK || 0.70); // «угол/высота» дуги

        // SVG у нас c viewBox 0..100 x 0..100, но для сохранения точной формы
        // заполним атрибуты эллипса именно в системе viewBox, а сам SVG растянется.
        if (arcSvg && arcEllipse) {
            arcSvg.setAttribute('viewBox', `0 0 100 100`);
            const cx_vb = 50;
            const cy_vb = 100 + 0.10; // слегка опускаем — убирает антиалиасинг-щель на краях
            const rx_vb = 50 + 0.25;  // доводим до самых краёв (50 = половина ширины)
            const ry_vb = (50) * k;   // ← радиус по Y задаёт «угол» дуги

            [arcEllipse, arcDash].forEach(el => {
                if (!el) return;
                el.setAttribute('cx', String(cx_vb));
                el.setAttribute('cy', String(cy_vb));
                el.setAttribute('rx', String(rx_vb));
                el.setAttribute('ry', String(ry_vb));
            });
        }

        // позиционирование кружков
        const itemSize = cfg.item;
        const actSize  = cfg.act;
        rail.classList.toggle('is-animating', false);

        // раскладка по дуге или линейная (мобилка)
        let activePos = null;

        if (cfg.layout === 'line') {
            const visible = cfg.visible; // 3
            const vis = windowIndices(active, N, visible);

            // базовые
            const baseItem = cfg.item;   // напр. 138
            const baseAct  = cfg.act;    // напр. 220
            const baseGap  = cfg.lineGap;// напр. 16

            const safePad = 10;                 // поля по краям
            const avail   = Math.max(140, W - safePad*2);
            let scale     = 1;

            // пробуем вписаться: центры = [cx - s, cx, cx + s], где s = r_act + r_item + gap
            const fit = sc => {
                const item = Math.max(60,  Math.round(baseItem * sc));
                const act  = Math.max(120, Math.round(baseAct  * sc));
                const gap  = Math.max(6,   Math.round(baseGap  * sc));

                const rI = item/2, rA = act/2;
                const s  = rA + rI + gap;                  // расстояние ОТ центра до соседа
                const leftEdge  = (W/2 - s) - rI;          // край левого маленького
                const rightEdge = (W/2 + s) + rI;          // край правого маленького
                const ok = (leftEdge >= safePad) && (rightEdge <= W - safePad);
                return { ok, item, act, gap, s };
            };

            let m = fit(scale);
            if (!m.ok) {
                // сколько нужно ужаться, чтобы вписаться
                const need = W - safePad*2;
                const have = (baseAct/2 + baseItem/2 + baseGap) * 2 + baseItem; // грубая оценка ширины
                scale = Math.max(0.4, Math.min(1, need / have));
                m = fit(scale);
            }

            const { item, act, gap, s } = m;
            const cx = W/2;
            const y  = H * 0.36; // чуть выше — больше места под текст

            // координаты центров
            const centers = [cx - s, cx, cx + s];

            let activePos = null;
            items.forEach((btn, i) => {
                const idxInVis = vis.indexOf(i);
                btn.classList.remove('is-visible','is-active');

                if (idxInVis !== -1) {
                    const isAct = (i === active);
                    const size  = isAct ? act : item;
                    const x     = centers[idxInVis];
                    const yPos  = y;

                    btn.style.width  = `${size}px`;
                    btn.style.height = `${size}px`;
                    btn.style.left   = `${Math.round(x)}px`;
                    btn.style.top    = `${Math.round(yPos)}px`;
                    btn.classList.add('is-visible');
                    if (isAct) {
                        btn.classList.add('is-active');
                        activePos = { x, y: yPos, actSize: act };
                        const ring = btn.querySelector('.ring-progress');
                        if (ring) { ring.style.animation = 'none'; ring.offsetHeight; ring.style.animation = `ring-progress var(--interval) linear forwards`; }
                    }
                }
            });

            // копи-блок: по центру экрана, не ниже 60% высоты сцены и с зазором от круга
// копи-блок (МОБИЛКА)
            if (copyEl && activePos && cfg.layout === 'line') {
                const minGap = 28; // отступ от низа активного круга до текста (px)
                const topByCircle = activePos.y + activePos.actSize / 2 + minGap;

                // ещё небольшой якорь, чтобы текст не опускался слишком низко
                const minBand = H * 0.48;             // не выше середины сцены
                const top = Math.max(topByCircle, minBand);

                copyEl.style.left = `${Math.round(W / 2)}px`;
                copyEl.style.top  = `${Math.round(top)}px`;
                copyEl.style.transform = 'translate(-50%, 0)'; // якорим ВЕРХ, не центр!
            }


            // выходим, чтобы «дуговая» логика ниже не шла
            return;
        }
 else {
            // дуга: считаем эллипс в пикселях сцены, чтобы координаты x/y совпадали
            const cx = W / 2;
            const cy = H; // низ сцены
            const rx = W / 2;         // горизонтальный радиус для позиций кружков
            // Вертикальный радиус для позиций — синхроним с «углом» маски:
            const k = Number(root.dataset.ellipseK || 0.70);
            const aspect = H / W;                 // коэффициент растяжения по вертикали (preserveAspectRatio="none")
            const ry = rx * k * aspect;

            // ВНИМАНИЕ: отступы по краям (edge) для слайдов НЕ трогаем (как просил).
            const edgeToCircleEdge = cfg.edge ?? 0;  // px до края крайнего круга
            const halfMinCircle    = itemSize / 2;  // страховка, как было
            const edgeInsetPx      = Math.max(0, Math.min(rx - 1, edgeToCircleEdge + halfMinCircle));
            const edgeInsetDeg     = (180 / Math.PI) * Math.acos((rx - edgeInsetPx) / rx);

            const START_DEG = Number(cfg.start) + edgeInsetDeg;
            const END_DEG   = Number(cfg.end)   - edgeInsetDeg;
            const SLOT_STEP = (END_DEG - START_DEG) / (cfg.visible - 1);

            // хотим, чтобы средний видимый пришёл в 270° (верх дуги) — это как у тебя
            const MID_DEG = 270;
            const offset  = MID_DEG - (START_DEG + Math.floor(cfg.visible / 2) * SLOT_STEP);

            // подготовка
            items.forEach(btn => {
                btn.classList.remove('is-visible', 'is-active');
                const r = btn.querySelector('.ring-progress');
                if (r) r.style.animation = 'none';
            });

            const vis = windowIndices(active, N, cfg.visible);

            vis.forEach((idx, k) => {
                const btn = items[idx];
                const isAct = (idx === active);
                const deg = START_DEG + k * SLOT_STEP + offset;
                const rad = toRad(deg);

                const x = cx + rx * Math.cos(rad);
                const y = cy + ry * Math.sin(rad) - (cfg.lift || 0); // лёгкий подъём

                btn.style.width = `${isAct ? actSize : itemSize}px`;
                btn.style.height = `${isAct ? actSize : itemSize}px`;
                btn.style.left = `${Math.round(x)}px`;
                btn.style.top  = `${Math.round(y)}px`;
                btn.classList.add('is-visible');
                if (isAct) {
                    btn.classList.add('is-active');
                    activePos = { x, y };
                    const ring = btn.querySelector('.ring-progress');
                    if (ring) { ring.style.animation = 'none'; ring.offsetHeight; ring.style.animation = `ring-progress var(--interval) linear forwards`; }
                }
            });
        }

        // копирайт-блок под активным
// копиблок
        if (copyEl && activePos) {
            const css = getComputedStyle(root);
            const gapBelow = parseFloat(css.getPropertyValue('--copyGap')) || (window.innerWidth >= 1600 ? 77 : 45);
            const actSizeCss = parseFloat(css.getPropertyValue('--activeSize')) || cfg.act;

            if (cfg.layout === 'line') {
                // Мобилка: центрируем по ширине контейнера и ограничиваем по высоте
                const xCenter = W / 2;
                // не опускаем текст ниже ~60% высоты сцены и не прилепляем вплотную к кругу
                const byCircle = activePos.y + Math.min(actSizeCss * 0.36 + 12, 56);
                const cap = H * 0.60;
                const top = Math.min(cap, byCircle);

                copyEl.style.left = `${Math.round(xCenter)}px`;
                copyEl.style.top  = `${Math.round(top)}px`;
                copyEl.style.transform = 'translate(-50%, -50%)';
            } else {
                // Десктоп/планшет: как было — под активным кругом
                const top = activePos.y + actSizeCss / 2 + gapBelow;
                copyEl.style.left = `${Math.round(activePos.x)}px`;
                copyEl.style.top  = `${Math.round(top)}px`;
                copyEl.style.transform = 'translate(-50%,0)';
            }
        }


        if (debug) {
            ensureDebugSvg();
            if (dbgSvg) {
                dbgSvg.innerHTML = '';
                const NS = 'http://www.w3.org/2000/svg';
                const r = rect;
                // рисуем габариты, центр и каркас эллипса
                const line = (x1,y1,x2,y2, stroke='#00f') => {
                    const el = document.createElementNS(NS,'line');
                    el.setAttribute('x1', x1);
                    el.setAttribute('y1', y1);
                    el.setAttribute('x2', x2);
                    el.setAttribute('y2', y2);
                    el.setAttribute('stroke', stroke);
                    el.setAttribute('stroke-width', '1');
                    dbgSvg.appendChild(el);
                };
                const circ = (cx,cy,rr, stroke='#0a0') => {
                    const el = document.createElementNS(NS,'circle');
                    el.setAttribute('cx', cx);
                    el.setAttribute('cy', cy);
                    el.setAttribute('r', rr);
                    el.setAttribute('fill', 'none');
                    el.setAttribute('stroke', stroke);
                    el.setAttribute('stroke-width', '1');
                    dbgSvg.appendChild(el);
                };
                // visual center
                // no precise mapping between viewBox and pixel ellipse here; оставим только вспомогательные линии
                line(0, H, W, H, '#f00'); // baseline
            }
        }
    }

    function animateTo(newActive) {
        if (newActive === active) return;
        const delta = newActive - active;
        const cw  = (delta + N) % N;
        const ccw = (N - cw) % N;
        const steps = cw <= ccw ? cw : -ccw;

        active = ((active + steps) % N + N) % N;  // ← ОБЯЗАТЕЛЬНО
        updateCopyFrom(active);
        rail.classList.add('is-animating');
        render();
        rail.addEventListener('transitionend', () => rail.classList.remove('is-animating'), { once: true });
    }
    function updateCopyFrom(index) {
        if (!copyEl) return;
        const btn = items[index];
        if (!btn) return;
        const title = btn.dataset.title || '';
        const desc  = btn.dataset.desc  || '';
        const tEl = copyEl.querySelector('.value-title');
        const dEl = copyEl.querySelector('.value-description');
        if (tEl) tEl.textContent = title;
        if (dEl) dEl.textContent = desc;
    }

    // клики
    rail.addEventListener('click', (e) => {
        const btn = e.target.closest('.values__item');
        if (!btn) return;
        const idx = items.indexOf(btn);
        if (idx === -1) return;
        animateTo(idx);
    });

    // запуск
    window.addEventListener('resize', render, { passive: true });
    render();
    updateCopyFrom(active);
    if (autoplay) startAutoplay();
}

